import { Box, Card, CardContent, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { WeekMenuProduct } from "../../../weekMenuTypes"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlateUtensils } from "@fortawesome/pro-light-svg-icons"
import { Stack } from "@mui/system"
import { StandardChip } from "../../../../../../../shared/components/badges/StandardChip"
import { ProductDisabledCover } from "../../../../../../../shared/components/productAvailability/ProductDisabledCover"
import { ProductCardInfo } from "./components/ProductCardInfo"

type StyleProps = {
    showBorder: boolean
    onlyView?: boolean
    extendDetails?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, { showBorder, onlyView, extendDetails }) => ({
    card: {
        minWidth: "17.5rem",
        width: extendDetails ? undefined : "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: onlyView ? "default" : "pointer",
        transition: "all 0.2s ease",
        borderRadius: !showBorder ? 0 : ".75rem",
        border: !showBorder ? "none" : `1px solid ${theme.palette.tailwind.gray[200]}`,
        position: "relative",
        overflow: "hidden",
        "&:hover": {
            transform: onlyView ? "none" : "translateY(-2px)",
        },
    },
    noImagePlaceholder: {
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: theme.palette.tailwind.slate[200],
    },
    noImagePlaceholderIcon: {
        height: "2rem",
        color: theme.palette.tailwind.slate[400],
    },
    imageContainer: {
        width: "100%",
        height: "10rem",
        overflow: "hidden",
        borderRadius: ".75rem .75rem 0 0",
        "& img": {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        },
    },
    content: {
        padding: !showBorder ? 0 : "1rem",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
    },
    title: {
        fontSize: "1.125rem",
        fontWeight: 600,
        lineHeight: 1.3,
        color: theme.palette.tailwind.slate[800],
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
    },
    description: {
        color: theme.palette.tailwind.slate[500],
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
    },
    price: {
        fontSize: "1rem",
        fontWeight: 600,
        color: theme.palette.tailwind.slate[800],
        marginTop: "auto",
    },
}))

type ProductCardProps = {
    resource: WeekMenuProduct
    onCardClick?: () => void
    imageMode: "none" | "mixed" | "unknown"
    extendDetails?: boolean
    onlyView?: boolean
}

export function ProductCard({ resource, onlyView, imageMode, extendDetails, onCardClick }: ProductCardProps) {
    const { classes, cx } = useStyles({ showBorder: imageMode !== "none", onlyView, extendDetails })
    const hasImage = Boolean(resource.imageUrl)

    return (
        <Card className={classes.card} onClick={onCardClick} elevation={0}>
            <ProductDisabledCover availability={resource.orderDetails?.availability} />
            {imageMode !== "none" &&
                (hasImage ? (
                    <Box className={classes.imageContainer}>
                        <img src={resource.imageUrl || "/"} alt={resource.subject} />
                    </Box>
                ) : (
                    <Stack className={cx(classes.imageContainer, classes.noImagePlaceholder)}>
                        <FontAwesomeIcon icon={faPlateUtensils} className={classes.noImagePlaceholderIcon} />
                    </Stack>
                ))}

            <CardContent className={classes.content}>
                {extendDetails ? (
                    <ProductCardInfo resource={resource} />
                ) : (
                    <>
                        {resource.headerTag && (
                            <Box>
                                <StandardChip label={resource.headerTag} variant="success" size="small" />
                            </Box>
                        )}
                        <Tooltip title={resource.subject} arrow>
                            <Typography className={classes.title} component="div">
                                {resource.subject}
                            </Typography>
                        </Tooltip>
                    </>
                )}
            </CardContent>
        </Card>
    )
}
